import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { PlusOutlined, DeleteOutlined, UploadOutlined, CaretDownOutlined } from '@ant-design/icons';
import MainCard from 'components/MainCard';
import LocationsManager from 'components/LocationsManager';
import LogoAdjustModal from 'components/LogoAdjustModal';
import buildImgTransform from 'utils/imgTransform';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

// Section definitions with their content schema
const SECTION_DEFS = [
  {
    type: 'hero',
    label: 'Hero Section',
    desc: 'Main banner — background image, tagline, and subtitle',
    fields: [
      { key: 'background_image', label: 'Background Banner Image', type: 'image' },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'subtext', label: 'Subtitle / Description', type: 'textarea' },
      { key: 'extra_paragraphs', label: 'Extra Paragraphs', type: 'list' },
    ],
  },
  {
    type: 'why_choose_us',
    label: 'Why Choose Us',
    desc: 'List of reasons customers should choose you',
    fields: [{ key: 'items', label: 'Reasons', type: 'list' }],
  },
  {
    type: 'conditions',
    label: 'Conditions',
    desc: 'Conditions you treat / handle',
    fields: [
      { key: 'items', label: 'Conditions', type: 'list' },
      { key: 'tagline', label: 'Section Tagline', type: 'text' },
    ],
  },
  {
    type: 'about',
    label: 'About Us',
    desc: 'Who you are, your approach, and clinic gallery photos',
    fields: [
      { key: 'who_we_are', label: 'Who We Are', type: 'textarea' },
      { key: 'who_we_are_bullets', label: 'Key Points', type: 'list' },
      { key: 'who_we_are_note', label: 'Additional Note', type: 'text' },
      { key: 'approach', label: 'Our Approach', type: 'list' },
      { key: 'approach_note', label: 'Approach Note', type: 'text' },
      { key: 'mission', label: 'Mission Statement', type: 'textarea' },
      { key: 'gallery_images', label: 'Clinic Photos (collage)', type: 'image_list', maxImages: 4, adjustKey: 'gallery_images_adjust' },
    ],
  },
  {
    type: 'doctors',
    label: 'Meet Our Doctors',
    desc: 'Public doctor profiles, shown right after About Us when enabled',
    custom: 'doctors',
    fields: [],
  },
  {
    type: 'gallery',
    label: 'Photo Gallery / Carousel',
    desc: 'Slideshow carousel of clinic/team photos shown on the site',
    fields: [
      { key: 'title', label: 'Gallery Section Title', type: 'text' },
      { key: 'images', label: 'Carousel Photos', type: 'image_list', maxImages: 10 },
    ],
  },
  {
    type: 'features',
    label: 'Services',
    desc: 'Service cards with title, description, and icon (shown as "What We Offer")',
    fields: [{ key: 'items', label: 'Services', type: 'object_list', objectFields: ['title', 'desc', 'icon'] }],
  },
  // 'locations' is rendered via a custom component (LocationsManager) that
  // edits the structured Location model — same records the doctor schedule UI
  // and Company Settings use. See the special-cased render in the accordion loop.
  {
    type: 'locations',
    label: 'Locations / Branches',
    desc: 'Your clinic branches (synced with Company Settings → Locations)',
    custom: 'locations',
    fields: [],
  },
  {
    type: 'stats',
    label: 'Website Stats',
    desc: 'Numbers shown on your site. Leave blank to auto-calculate from bookings, set 0 to hide a stat. Doctor count is always automatic.',
    fields: [
      { key: 'bookings', label: 'Total Bookings', type: 'number' },
      { key: 'users', label: 'Happy Patients', type: 'number' },
    ],
  },
  {
    type: 'footer',
    label: 'Footer',
    desc: 'Footer tagline, contact intro, and hours',
    fields: [
      { key: 'tagline', label: 'Footer Tagline', type: 'text' },
      { key: 'contact_intro', label: 'Contact Introduction', type: 'textarea' },
      { key: 'consultation_hours', label: 'Consultation Hours', type: 'text' },
    ],
  },
  {
    type: 'closing_cta',
    label: 'Closing CTA',
    desc: 'Final call-to-action message',
    fields: [{ key: 'text', label: 'CTA Text', type: 'textarea' }],
  },
];

// ── Image Upload Component ────────────────────────────────────────────────────
function ImageUpload({ value, onChange, label, companyId, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(endpoints.company.uploadSectionImage(companyId), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(res.data.url);
    } catch {
      setUploadError('Upload failed. Try again or paste a URL below.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {/* Preview */}
        {value && (
          <Box
            component="img"
            src={value}
            alt="preview"
            sx={{ width: 120, height: 75, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee', flexShrink: 0 }}
          />
        )}
        <Stack spacing={1} flex={1}>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={14} /> : <UploadOutlined />}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : value ? 'Replace Image' : 'Upload Image'}
            </Button>
            {value && (
              <IconButton size="small" color="error" onClick={() => onChange('')}>
                <DeleteOutlined />
              </IconButton>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="…or paste image URL"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
          />
          {uploadError && <Typography variant="caption" color="error">{uploadError}</Typography>}
        </Stack>
      </Stack>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files[0])} />
    </Box>
  );
}

// ── Image List (multiple photos) ──────────────────────────────────────────────
function ImageList({ images = [], onChange, label, companyId, token, maxImages = 10, adjust = {}, onAdjustChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [adjustUrl, setAdjustUrl] = useState(null); // image being repositioned

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(endpoints.company.uploadSectionImage(companyId), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange([...images, res.data.url]);
    } catch {
      setUploadError('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const handleUrlAdd = () => onChange([...images, '']);
  const handleUrlChange = (i, val) => {
    const updated = [...images];
    updated[i] = val;
    onChange(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      {/* Thumbnails */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {images.map((url, i) => (
          <Box key={i} sx={{ position: 'relative' }}>
            {url ? (
              <Box sx={{ width: 90, height: 60, borderRadius: 1, border: '1px solid #eee', overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={url}
                  alt={`photo ${i + 1}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: buildImgTransform(adjust[url]) }}
                />
              </Box>
            ) : (
              <Box sx={{ width: 90, height: 60, borderRadius: 1, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">URL</Typography>
              </Box>
            )}
            <IconButton
              size="small"
              color="error"
              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #eee', width: 20, height: 20 }}
              onClick={() => handleRemove(i)}
            >
              <DeleteOutlined style={{ fontSize: 10 }} />
            </IconButton>
            {url && onAdjustChange && (
              <Button size="small" onClick={() => setAdjustUrl(url)}
                sx={{ mt: 0.25, minWidth: 0, width: 90, fontSize: '0.62rem', py: 0, lineHeight: 1.6 }}>
                Adjust
              </Button>
            )}
            {!url && (
              <TextField
                size="small"
                placeholder="Paste URL"
                sx={{ mt: 0.5, width: 90 }}
                inputProps={{ style: { fontSize: '0.7rem', padding: '4px 6px' } }}
                onChange={(e) => handleUrlChange(i, e.target.value)}
              />
            )}
          </Box>
        ))}
      </Box>
      {images.length < maxImages && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <UploadOutlined />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload Photo'}
          </Button>
          <Button size="small" startIcon={<PlusOutlined />} onClick={handleUrlAdd}>
            Add URL
          </Button>
        </Stack>
      )}
      {uploadError && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{uploadError}</Typography>}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files[0])} />
      {onAdjustChange && (
        <LogoAdjustModal
          open={!!adjustUrl}
          onClose={() => setAdjustUrl(null)}
          src={adjustUrl}
          fit="cover"
          title="Adjust photo"
          label="photo"
          scale={adjust[adjustUrl]?.scale || 1}
          position={adjust[adjustUrl]?.position || '0% 0%'}
          onApply={({ scale, position }) => onAdjustChange({ ...adjust, [adjustUrl]: { scale, position } })}
        />
      )}
    </Box>
  );
}

// ── Dynamic text list ─────────────────────────────────────────────────────────
function DynamicList({ items = [], onChange, label }) {
  const handleChange = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };
  const handleAdd = () => onChange([...items, '']);
  const handleRemove = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Stack spacing={1}>
        {items.map((item, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth size="small" value={item}
              onChange={(e) => handleChange(i, e.target.value)}
              placeholder={`${label} item ${i + 1}`}
            />
            <IconButton size="small" color="error" onClick={() => handleRemove(i)}>
              <DeleteOutlined />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button startIcon={<PlusOutlined />} size="small" onClick={handleAdd} sx={{ mt: 1 }}>
        Add {label}
      </Button>
    </Box>
  );
}

// ── Doctors section editor: toggle + title + read-only doctor list ────────────
function DoctorsSectionEditor({ content, updateField }) {
  const token = localStorage.getItem('token');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const enabled = content?.enabled !== false; // default ON
  const title = content?.title || 'Meet Our Doctors';

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(endpoints.company.doctorList(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDoctors(Array.isArray(resp.data) ? resp.data : (resp.data?.results || []));
      } catch {
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleCount = doctors.filter((d) => d.is_active && d.show_on_website !== false).length;
  const [savingId, setSavingId] = useState(null);

  const toggleVisibility = async (doc) => {
    const next = !(doc.show_on_website !== false);
    setSavingId(doc.id);
    // optimistic update
    setDoctors((prev) => prev.map((x) => x.id === doc.id ? { ...x, show_on_website: next } : x));
    try {
      await axios.patch(
        endpoints.company.updateDoctor(doc.id),
        { show_on_website: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // revert on failure
      setDoctors((prev) => prev.map((x) => x.id === doc.id ? { ...x, show_on_website: !next } : x));
    } finally {
      setSavingId(null);
    }
  };

  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const persistOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      // Persist each doctor's new position (index) as display_order.
      await Promise.all(ordered.map((d, idx) =>
        d.display_order === idx ? null : axios.patch(
          endpoints.company.updateDoctor(d.id),
          { display_order: idx },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ).filter(Boolean));
      setDoctors(ordered.map((d, idx) => ({ ...d, display_order: idx })));
    } catch {
      // best-effort; local order already reflects the drop
    } finally {
      setSavingOrder(false);
    }
  };

  const onDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const next = [...doctors];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setDoctors(next);
    setDragIndex(null);
    persistOrder(next);
  };

  return (
    <Stack spacing={2.25}>
      <Alert severity="info" sx={{ py: 0.5 }}>
        Drag the ⠿ handle to reorder how doctors appear on the website. Toggle each doctor's visibility with the switch —
        it's the same show/hide setting as in the <strong>Doctors</strong> menu.
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => updateField('doctors', 'enabled', e.target.checked)}
          style={{ transform: 'scale(1.2)', accentColor: '#1976d2', cursor: 'pointer' }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Show "Meet Our Doctors" section on the public website
        </Typography>
      </Box>

      <TextField
        size="small" fullWidth label="Section title"
        placeholder="Meet Our Doctors"
        value={title}
        onChange={(e) => updateField('doctors', 'title', e.target.value)}
        helperText="Displayed as the heading of the section on the public site."
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {loading ? 'Loading doctors…' : `Currently ${visibleCount} of ${doctors.length} doctors will appear on the website`}
        </Typography>
        {!loading && doctors.length === 0 && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            No doctors yet. Add doctors via the Doctors menu first.
          </Alert>
        )}
        <Stack spacing={1}>
          {doctors.map((d, index) => {
            const showing = d.is_active && d.show_on_website !== false;
            return (
              <Box key={d.id}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(index)}
                onDragEnd={() => setDragIndex(null)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.25, border: '1px solid', borderColor: dragIndex === index ? 'primary.main' : 'divider', borderRadius: 1, bgcolor: showing ? 'rgba(46,125,50,0.04)' : 'rgba(0,0,0,0.02)', opacity: savingOrder ? 0.7 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ cursor: 'grab', color: 'text.disabled', fontSize: '1.1rem', lineHeight: 1, userSelect: 'none', px: 0.25 }} title="Drag to reorder">⠿</Box>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    bgcolor: 'primary.50', color: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, overflow: 'hidden',
                    ...(d.photo ? { backgroundImage: `url(${d.photo})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' } : {}),
                  }}>
                    {d.name?.charAt(0)?.toUpperCase() || '?'}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.specialization_name || '—'}
                      {d.qualifications ? ` · ${d.qualifications}` : ''}
                    </Typography>
                  </Box>
                </Box>
                {!d.is_active ? (
                  <Chip size="small" label="Inactive" color="default" variant="outlined" />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {d.show_on_website === false ? 'Hidden' : 'Showing'}
                    </Typography>
                    <Tooltip title={d.show_on_website === false ? 'Show on website' : 'Hide from website'}>
                      <Switch
                        size="small"
                        checked={d.show_on_website !== false}
                        disabled={savingId === d.id}
                        onChange={() => toggleVisibility(d)}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}

function ObjectList({ items = [], onChange, label, objectFields }) {
  const handleChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };
  const handleAdd = () => {
    const empty = {};
    objectFields.forEach((f) => (empty[f] = ''));
    onChange([...items, empty]);
  };
  const handleRemove = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Stack spacing={2}>
        {items.map((item, i) => (
          <Box key={i} sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 1, position: 'relative' }}>
            <IconButton size="small" color="error" onClick={() => handleRemove(i)}
              sx={{ position: 'absolute', top: 4, right: 4 }}>
              <DeleteOutlined />
            </IconButton>
            <Stack spacing={1}>
              {objectFields.map((field) => (
                <TextField
                  key={field} fullWidth size="small" label={field}
                  value={item[field] || ''} onChange={(e) => handleChange(i, field, e.target.value)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Button startIcon={<PlusOutlined />} size="small" onClick={handleAdd} sx={{ mt: 1 }}>
        Add {label}
      </Button>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function WebsiteContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [sections, setSections] = useState({});
  const [expanded, setExpanded] = useState('hero');

  const tenantId = localStorage.getItem('tenant_id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const res = await axios.get(endpoints.company.websiteSections(tenantId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const dict = {};
      (res.data || []).forEach((s) => {
        dict[s.section_type] = s.content || {};
      });
      setSections(dict);
    } catch {
      setSections({});
    } finally {
      setLoading(false);
    }
  };

  const updateField = (sectionType, key, value) => {
    setSections((prev) => ({
      ...prev,
      [sectionType]: {
        ...(prev[sectionType] || {}),
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await axios.post(
        endpoints.company.websiteSectionsBulk(tenantId),
        { sections },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Website content saved successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save website content');
    } finally {
      setSaving(false);
    }
  };

  const filledCount = SECTION_DEFS.filter((def) => {
    const content = sections[def.type];
    if (!content) return false;
    return Object.values(content).some((v) =>
      Array.isArray(v) ? v.length > 0 : v && v.toString().trim() !== ''
    );
  }).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container rowSpacing={4.5} columnSpacing={2.75}>
      <Grid sx={{ mb: -2.25 }} size={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5">Website Content</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage what appears on your public appointment booking page
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${filledCount}/${SECTION_DEFS.length} sections`} size="small" color="primary" variant="outlined" />
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 120 }}>
              {saving ? <CircularProgress size={20} /> : 'Save All'}
            </Button>
          </Stack>
        </Stack>
      </Grid>

      <Grid size={12}>
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <MainCard sx={{ p: 0 }}>
          {SECTION_DEFS.map((def) => {
            const content = sections[def.type] || {};
            const hasContent = Object.values(content).some((v) =>
              Array.isArray(v) ? v.length > 0 : v && v.toString().trim() !== ''
            );

            return (
              <Accordion
                key={def.type}
                expanded={expanded === def.type}
                onChange={(_, isExpanded) => setExpanded(isExpanded ? def.type : false)}
                disableGutters
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<CaretDownOutlined style={{ fontSize: 16, color: '#475569' }} />}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{def.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{def.desc}</Typography>
                    {hasContent && (
                      <Chip label="Active" size="small" color="success" variant="outlined" sx={{ ml: 'auto', mr: 2 }} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2.5}>
                    {def.custom === 'locations' && (
                      <LocationsManager tenantId={tenantId} />
                    )}
                    {def.custom === 'doctors' && (
                      <DoctorsSectionEditor content={content} updateField={updateField} />
                    )}
                    {def.fields.map((field) => {
                      if (field.type === 'image') {
                        return (
                          <ImageUpload
                            key={field.key}
                            label={field.label}
                            value={content[field.key] || ''}
                            onChange={(val) => updateField(def.type, field.key, val)}
                            companyId={tenantId}
                            token={token}
                          />
                        );
                      }
                      if (field.type === 'image_list') {
                        return (
                          <ImageList
                            key={field.key}
                            label={field.label}
                            images={content[field.key] || []}
                            onChange={(val) => updateField(def.type, field.key, val)}
                            companyId={tenantId}
                            token={token}
                            maxImages={field.maxImages}
                            adjust={field.adjustKey ? (content[field.adjustKey] || {}) : {}}
                            onAdjustChange={field.adjustKey ? (val) => updateField(def.type, field.adjustKey, val) : undefined}
                          />
                        );
                      }
                      if (field.type === 'list') {
                        return (
                          <DynamicList
                            key={field.key}
                            label={field.label}
                            items={content[field.key] || []}
                            onChange={(val) => updateField(def.type, field.key, val)}
                          />
                        );
                      }
                      if (field.type === 'object_list') {
                        return (
                          <ObjectList
                            key={field.key}
                            label={field.label}
                            items={content[field.key] || []}
                            objectFields={field.objectFields}
                            onChange={(val) => updateField(def.type, field.key, val)}
                          />
                        );
                      }
                      if (field.type === 'textarea') {
                        return (
                          <TextField
                            key={field.key}
                            fullWidth size="small" label={field.label}
                            multiline rows={3}
                            value={content[field.key] || ''}
                            onChange={(e) => updateField(def.type, field.key, e.target.value)}
                          />
                        );
                      }
                      if (field.type === 'number') {
                        return (
                          <TextField
                            key={field.key}
                            fullWidth size="small" type="number" label={field.label}
                            value={content[field.key] ?? ''}
                            onChange={(e) => updateField(def.type, field.key, e.target.value)}
                            helperText="Leave blank to auto-calculate · enter 0 to hide this stat"
                            inputProps={{ min: 0 }}
                          />
                        );
                      }
                      return (
                        <TextField
                          key={field.key}
                          fullWidth size="small" label={field.label}
                          value={content[field.key] || ''}
                          onChange={(e) => updateField(def.type, field.key, e.target.value)}
                        />
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </MainCard>
      </Grid>
    </Grid>
  );
}
